import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, PillTabs, PullToRefresh, ExplanationNote, ResizedImage, type PillTab } from '@ah/design/mobile';
import { Users, Sparkles, Clock } from 'lucide-react';
import {
  MY_CIRCLES_QUERY,
  circleDisplayName,
  type HumanCircle,
} from '../shared/index.ts';
import { CircleListSkeleton, ErrorRetry } from './_parts.tsx';

const TABS: PillTab[] = [
  { to: '/circles', label: 'My Circles', end: true },
  { to: '/circles/suggested', label: 'Suggested' },
];

export function Circles() {
  const gql = useGql();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['circles', 'my'],
    queryFn: async () => {
      const res = await gql.request<{ myCircles: HumanCircle[] }>(MY_CIRCLES_QUERY);
      return res.myCircles;
    },
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Circles" />
      <PillTabs tabs={TABS} />
      <PullToRefresh
        className="flex-1 px-3 py-3"
        onRefresh={() => refetch()}
      >
        <ExplanationNote storageKey="circles.my">
          Circles are global communities organised around a shared identity —
          your alumni network, your industry, a craft or cause. Some you're
          added to automatically; others you can join from the Suggested tab.
        </ExplanationNote>
        {isLoading ? (
          <CircleListSkeleton />
        ) : error ? (
          <ErrorRetry label="Could not load circles." onRetry={() => refetch()} />
        ) : !data || data.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Users size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium">You haven't joined any circles yet.</p>
            <Link
              to="/circles/suggested"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium"
            >
              <Sparkles size={14} /> Browse Suggested
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {data.map((hc) => (
              <CircleRow key={hc.id} hc={hc} />
            ))}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}

function CircleRow({ hc }: { hc: HumanCircle }) {
  const { circle } = hc;
  const isInvited = hc.status === 'invited' || hc.status === 'pending' || hc.status === 'inferred';
  const cover = circle.coverUrl;

  return (
    <Link
      to={`/circles/${circle.id}`}
      className="flex gap-3 items-center p-3 bg-card rounded-xl border border-border active:bg-accent"
    >
      <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
        {cover ? (
          <ResizedImage src={cover} renderWidth={56} fallback={false} alt="" className="w-full h-full object-cover" />
        ) : (
          <Users size={20} className="text-muted-foreground/60" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-semibold truncate">
            {circleDisplayName(circle.displayTitle ?? circle.name)}
          </h2>
          {isInvited ? (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
          ) : null}
        </div>
        {circle.description && (
          <p className="text-[12px] text-muted-foreground truncate mt-0.5">
            {circle.description}
          </p>
        )}
      </div>
      {isInvited ? (
        <span className="text-[10px] text-amber-600 font-medium shrink-0 inline-flex items-center gap-1">
          <Clock size={10} /> Invite
        </span>
      ) : null}
    </Link>
  );
}
